import {SquadflixHomePreferences} from "../../../user-app-config/hooks/";
import React, {useCallback, useMemo} from "react";
import useUserStorage from "../../../../hooks/useUserStorage";
import ContinueWatchingRow from "../rows/ContinueWatchingRow";
import SpanishLeagueEventsRow from "../rows/SpanishLeagueEventsRow";
import NewEpisodesRow from "../rows/NewEpisodesRow";
import auth from "../../../../utils/authentication";
import CollectionsItemsRow from "../rows/CollectionsItemsRow";
import {useIntl} from "react-intl";
import TrendingMoviesRow from "../rows/TrendingMoviesRow";
import TrendingSeriesRow from "../rows/TrendingSeriesRow";
import SquadflixItemsRow from "../rows/SquadflixItemsRow";
import TorrestreamItemsRow from "../rows/TorrestreamItemsRow";
import {StremioChannelsRow} from "../rows/StremioChannelsRow";

type Props = {
    preferences?: SquadflixHomePreferences;
    collections?: string[];
}

export enum SquadflixItemRowId {
    CONTINUE_WATCHING = "continue-watching",
    SPANISH_LEAGUE_EVENTS = "spanish-league-events",
    LIVE_CHANNELS = "live-channels",
    NEW_EPISODES = "new-episodes",
    TRENDING_MOVIES = "trending-movies",
    TRENDING_SERIES = "trending-series",
    SQUADFLIX_ORIGINALS = "squadflix-originals",
    TOWERSTREAM_ITEMS = "towerstream-items",
    VIEW_LATER = "view-later",
}

type ItemRow = {
    id: SquadflixItemRowId;
    visible: boolean;
    order: number;
}

const defaultValue = {};

const UseSquadflixItemsRows = ({preferences, collections}: Props) => {

    const orderStorage = useUserStorage({key: "squadflixHomeRowsOrder", defaultValue});
    const [ableReorder, setAbleReorder] = React.useState(false);
    const intl = useIntl();

    const itemsRows = useMemo(() => {
        if (!preferences) {
            return [];
        }

        const rows: ItemRow[] = [
            {
                id: SquadflixItemRowId.CONTINUE_WATCHING,
                visible: true,
                order: orderStorage.item?.[SquadflixItemRowId.CONTINUE_WATCHING] ?? 1,
            }, {
                id: SquadflixItemRowId.SPANISH_LEAGUE_EVENTS,
                visible: preferences.showLaLigaEventsSection ?? false,
                order: orderStorage.item?.[SquadflixItemRowId.SPANISH_LEAGUE_EVENTS] ?? 2,
            }, {
                id: SquadflixItemRowId.NEW_EPISODES,
                visible: preferences.showNewEpisodesSection ?? true,
                order: orderStorage.item?.[SquadflixItemRowId.NEW_EPISODES] ?? 3,
            }, {
                id: SquadflixItemRowId.VIEW_LATER,
                visible: preferences.showViewLaterItems ?? true,
                order: orderStorage.item?.[SquadflixItemRowId.VIEW_LATER] ?? 4,
            }, {
                id: SquadflixItemRowId.TRENDING_MOVIES,
                visible: preferences.showTrendingMoviesSection ?? false,
                order: orderStorage.item?.[SquadflixItemRowId.TRENDING_MOVIES] ?? 5,
            }, {
                id: SquadflixItemRowId.TRENDING_SERIES,
                visible: preferences.showTrendingSeriesSection ?? false,
                order: orderStorage.item?.[SquadflixItemRowId.TRENDING_SERIES] ?? 6,
            }, {
                id: SquadflixItemRowId.LIVE_CHANNELS,
                visible: preferences.showLiveTvSection ?? false,
                order: orderStorage.item?.[SquadflixItemRowId.LIVE_CHANNELS] ?? 7,
            }, {
                id: SquadflixItemRowId.SQUADFLIX_ORIGINALS,
                visible: preferences.showSquadflixOriginalsSection ?? true,
                order: orderStorage.item?.[SquadflixItemRowId.SQUADFLIX_ORIGINALS] ?? 8,
            }, {
                id: SquadflixItemRowId.TOWERSTREAM_ITEMS,
                visible: preferences.showTowersStreamSection ?? true,
                order: orderStorage.item?.[SquadflixItemRowId.TOWERSTREAM_ITEMS] ?? 9,
            }
        ];

        for (const collectionId of collections || []) {
            rows.push({
                id: collectionId as SquadflixItemRowId,
                visible: true,
                order: orderStorage.item?.[collectionId] ?? (rows.length + 1),
            });
        }

        const sortedRows = rows.sort((a, b) => {
            return a.order - b.order;
        });

        return sortedRows.map((row, i) => {
            return {...row, order: i + 1};
        })
    }, [preferences, orderStorage.item, collections]);

    const moveItemRow = useCallback((id: string, direction: "up" | "down") => {
        if (!itemsRows.length) {
            return;
        }
        const currentOrder = itemsRows.find(row => row.id === id)?.order;
        if (currentOrder === undefined) {
            return;
        }
        const findSwapRow = (orderIndex: number) => {
            const swapRow = itemsRows.find(row => row.order === orderIndex);
            if (!swapRow) {
                return null;
            }
            if (!swapRow.visible) {
                return findSwapRow(direction === "up" ? orderIndex - 1 : orderIndex + 1);
            }
            return swapRow;
        }
        const swapRow = findSwapRow(direction === "up" ? currentOrder - 1 : currentOrder + 1);
        console.log("swapRow", swapRow);
        if (!swapRow) {
            return;
        }
        const newOrders = {
            ...orderStorage.item,
            [id]: swapRow.order,
            [swapRow.id]: currentOrder
        };
        orderStorage.setItem(newOrders);
    }, [itemsRows, orderStorage]);

    const getDefaultProps = useCallback((id: string, defaultOrder: number): SquadflixRowsBaseProps => {
        const order = itemsRows.find(row => row.id === id)?.order ?? defaultOrder;
        return {
            showReorderControls: ableReorder,
            order: order,
            isFirst: order === 1,
            isLast: order === itemsRows.filter(row => row.visible).length,
            onMoveUp: () => {
                moveItemRow(id, "up");
            },
            onMoveDown: () => {
                moveItemRow(id, "down");
            }
        };
    }, [itemsRows, moveItemRow, ableReorder]);

    const getComponent = useCallback((id: string, handleHideCollection: (string) => void) => {
        switch (id) {
            case SquadflixItemRowId.CONTINUE_WATCHING:
                return <ContinueWatchingRow {...getDefaultProps(id, 1)} />;
            case SquadflixItemRowId.SPANISH_LEAGUE_EVENTS:
                return <SpanishLeagueEventsRow {...getDefaultProps(id, 2)}/>;
            case SquadflixItemRowId.NEW_EPISODES:
                return <NewEpisodesRow {...getDefaultProps(id, 3)}/>;
            case SquadflixItemRowId.VIEW_LATER:
                return <CollectionsItemsRow
                    {...getDefaultProps(id, 4)}
                    header={{
                        title: intl.formatMessage({id: "squadflix-home.view-later"}),
                        iconClass: "fa-solid fa-clock-rotate-left fa-flip-horizontal",
                    }}
                    collectionId={"view-later-" + auth.currentUser.uid}
                />
            case SquadflixItemRowId.TRENDING_MOVIES:
                return <TrendingMoviesRow {...getDefaultProps(id, 5)} />;
            case SquadflixItemRowId.TRENDING_SERIES:
                return <TrendingSeriesRow {...getDefaultProps(id, 6)}/>;
            case SquadflixItemRowId.LIVE_CHANNELS:
                return <StremioChannelsRow {...getDefaultProps(id, 7)}/>
            case SquadflixItemRowId.SQUADFLIX_ORIGINALS:
                return <SquadflixItemsRow {...getDefaultProps(id, 8)}/>
            case SquadflixItemRowId.TOWERSTREAM_ITEMS:
                return <TorrestreamItemsRow {...getDefaultProps(id, 9)}/>
            default:
                return <CollectionsItemsRow
                    {...getDefaultProps(id, 10)}
                    canEdit={true}
                    canHide={true}
                    onHide={() => handleHideCollection(id)}
                    header={{
                        title: "{{collectionName}}",
                        iconClass: "fa-solid fa-layer-group",
                    }}
                    collectionId={id}
                />
        }
    }, [getDefaultProps, intl]);

    return {
        itemsRows: itemsRows.filter(row => row.visible),
        getComponent,
        moveItemRow,
        ableReorder,
        setAbleReorder,
    };
}

export default UseSquadflixItemsRows;
